// Trillen bij tikken (oprichter 1 okt 2026): een korte trilling als iemand iets indrukt. Alleen
// waar navigator.vibrate bestaat (Android) of waar de app een eigen trilmanier zet (iPhone-app,
// setTapDriver); in iOS Safari en op tablets zonder motor doet dit niets en gooit het nooit. Eén
// globale luisteraar per app (installTapFeedback), plus sterkere patronen voor een afgevinkte taak,
// een gestarte timer en een PIN (goed of fout).

/** Gewone tik. */
export const TAP_MS = 10;
/** Iets sterker: taak klaar, timer gestart, PIN goed. */
export const TAP_STRONG_MS = 20;
/** Dubbele puls: PIN fout. */
export const TAP_ERROR: ReadonlyArray<number> = [15, 60, 15];

/** Elementen waarop een tik voelbaar is. */
export const TAP_SELECTOR = 'button, [role="button"], a, input[type="checkbox"], [data-open]';

let enabled = true;

/** Eigen manier van trillen (iPhone-app: Taptic Engine via Capacitor); true = afgehandeld. */
export type TapDriver = (pattern: number | ReadonlyArray<number>) => boolean;

let driver: TapDriver | null = null;

/** Aan of uit (instelling per bord of per gebruiker). */
export function setHapticsEnabled(on: boolean): void {
  enabled = on;
}

export function hapticsEnabled(): boolean {
  return enabled;
}

/** Zet een eigen trilmanier (of null: terug naar navigator.vibrate). Het bord gebruikt dit niet. */
export function setTapDriver(next: TapDriver | null): void {
  driver = next;
}

/** Trilt als het mag en kan; anders niets. Gooit nooit. */
export function tap(pattern: number | ReadonlyArray<number> = TAP_MS): void {
  if (!enabled) return;
  try {
    if (driver && driver(pattern)) return;
    const nav = typeof navigator === 'undefined' ? null : navigator;
    if (!nav || typeof nav.vibrate !== 'function') return;
    nav.vibrate(typeof pattern === 'number' ? pattern : pattern.slice());
  } catch {
    // Geen toestemming (nog geen gebruikersactie) of een browser die het weigert: stil.
  }
}

export function tapStrong(): void {
  tap(TAP_STRONG_MS);
}

export function tapError(): void {
  tap(TAP_ERROR);
}

/** Het interactieve element rond dit doel, of null (uitgeschakelde knoppen tellen niet). */
export function tapTarget(target: EventTarget | null): Element | null {
  const el = target as Element | null;
  if (!el || typeof el.closest !== 'function') return null;
  const hit = el.closest(TAP_SELECTOR);
  if (!hit) return null;
  if ((hit as HTMLButtonElement).disabled === true) return null;
  if (hit.getAttribute('aria-disabled') === 'true') return null;
  return hit;
}

/**
 * Eén luisteraar in de capture-fase op het hele document: touchstart (iOS 12 kent geen pointer
 * events; een touchstart-luisteraar zorgt daar ook dat :active werkt), met de muis als terugval.
 * Een muisklik vlak na een aanraking (de nagebootste klik van de browser) telt niet nog een keer.
 * Geeft een opruimer terug.
 */
export function installTapFeedback(doc: Document = document): () => void {
  let lastTouch = 0;
  const onTouch = (event: Event): void => {
    lastTouch = Date.now();
    if (tapTarget(event.target)) tap();
  };
  const onMouse = (event: Event): void => {
    if (Date.now() - lastTouch < 800) return;
    if (tapTarget(event.target)) tap();
  };
  const opts: AddEventListenerOptions = { capture: true, passive: true };
  doc.addEventListener('touchstart', onTouch, opts);
  doc.addEventListener('mousedown', onMouse, opts);
  return () => {
    doc.removeEventListener('touchstart', onTouch, opts);
    doc.removeEventListener('mousedown', onMouse, opts);
  };
}
